import React, { useLayoutEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pencil, Check, X } from 'lucide-react';

interface PoseNoteFieldClassNames {
  cell?: string;
  headerRow?: string;
  header: string;
  iconBtn: string;
  body: string;
  textarea: string;
}

interface PoseNoteFieldProps {
  label: string;
  value: string;
  emptyText: string;
  onSave: (value: string) => void;
  classNames: PoseNoteFieldClassNames;
  disabled?: boolean;
  /** Lets the parent know editing has started/stopped, e.g. to suppress hover-hide while typing. */
  onEditingChange?: (isEditing: boolean) => void;
  /** Element ids for callers that need to target the save button/textarea (e.g. the tutorial overlay). */
  ids?: { saveBtn?: string; textarea?: string };
  /** Pass to drive editing externally (e.g. a context-menu "Edit Note" action). Omit for self-managed editing. */
  isEditing?: boolean;
}

/**
 * Editable note cell (label + body, with inline edit/save/cancel) shared by the
 * Pose Library's hover preview and the Timeline's keyframe hover preview, per
 * docs/requirements/PosesLibrary.md "Pose Preview Control".
 */
export function PoseNoteField({ label, value, emptyText, onSave, classNames, disabled, onEditingChange, ids, isEditing: isEditingProp }: PoseNoteFieldProps) {
  const { t } = useTranslation();
  const [internalEditing, setInternalEditing] = useState(false);
  const isEditing = isEditingProp !== undefined ? isEditingProp : internalEditing;
  const [tempValue, setTempValue] = useState('');

  const setEditing = (next: boolean) => {
    setInternalEditing(next);
    onEditingChange?.(next);
  };

  // Seed the draft from the latest value whenever editing starts, whether
  // triggered by the Edit button or driven externally via the isEditing prop.
  useLayoutEffect(() => {
    if (isEditing) setTempValue(value || '');
  }, [isEditing]);

  const commit = () => {
    onSave(tempValue);
    setEditing(false);
  };

  if (isEditing) {
    return (
      <div className={classNames.cell}>
        <div className={classNames.headerRow}>
          <div className={classNames.header}>{label}</div>
          <div className="flex-row" style={{ gap: '6px' }}>
            <button
              className={classNames.iconBtn}
              title={t('common.cancel', { defaultValue: 'Cancel' })}
              onClick={() => setEditing(false)}
            >
              <X size={11} />
            </button>
            <button
              id={ids?.saveBtn}
              className={classNames.iconBtn}
              title={t('common.save', { defaultValue: 'Save' })}
              onClick={commit}
            >
              <Check size={11} />
            </button>
          </div>
        </div>
        <textarea
          id={ids?.textarea}
          autoFocus
          className={classNames.textarea}
          value={tempValue}
          onChange={(e) => setTempValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              commit();
            }
            if (e.key === 'Escape') {
              setEditing(false);
            }
          }}
        />
      </div>
    );
  }

  const trimmed = value.trim();
  return (
    <div className={classNames.cell}>
      <div className={classNames.headerRow}>
        <div className={classNames.header}>{label}</div>
        <button
          className={classNames.iconBtn}
          disabled={disabled}
          title={t('timeline.edit_btn', { defaultValue: 'Edit' })}
          onClick={() => setEditing(true)}
        >
          <Pencil size={11} />
        </button>
      </div>
      <div className={`${classNames.body}${!trimmed ? ' empty' : ''}`}>
        {trimmed || emptyText}
      </div>
    </div>
  );
}
